import { useEffect, useMemo, useState } from "react";
import { Box, HStack } from "@chakra-ui/react";
import MeasureDistance from "./MeasureDistance";
import ShowPoints from "./ShowPoints";
import GlobalTools from "./GlobalTools";
import Lanelet from "./Lanelet";
import { useAppStore } from "../../../context/store";
import Type from "../../../utilies/Types";
import Association from "./Association";
import Arc from "./Arc";
import PointCloud from "./PointCloud";
import QuickSearch from "./QuickSearch";
import GenerateRoute from "./GenerateRoute";
import LineIntersectMeasure from "./LineIntersectMeasure";
import OverlayText from "./OverlayText";
import RoadMarking from "./RoadMarking";
import ValidationTool from "./ValidationTool";
import TrafficLightTool from "./TrafficLightTool";
import { useMapObjects } from "../../../context/MapObjectsContext";
import TrafficSignTool from "./TrafficSignTool";
import AutoSketcher from "./AutoSketcher";
import CustomizeTools from "./CustomizeTools";
import AreaCalculation from "./AreaCalculation";
import DrivewayTool from "./DrivewayTool";
import MoveTool from "./MoveTool";
import LaneMarkerStyleTool from "./LaneMarkerStyleTool";
import TurningArcTool from "./TurningArcTool";
import LaneConnectorTool from "./LaneConnectorTool";
import LaneletCheckByIds from "./LaneletCheckByIds";
import AutomaticConnectionCheck from "./LaneletAutomaticConnectionCheck";

function SideBarRight() {
  const TOOL_IDS = {
    measureDistance: "measureDistance",
    lineIntersectMeasure: "lineIntersectMeasure",
    quickSearch: "quickSearch",
    showPoints: "showPoints",
    validationMenu: "validationMenu",
    globalTools: "globalTools",
    overlayText: "overlayText",
    trafficLight: "trafficLight",
    trafficSign: "trafficSign",
    lanelet: "lanelet",
    LaneletCheckByIds: "LaneletCheckByIds",
    AutomaticConnectionCheck: "AutomaticConnectionCheck",
    association: "association",
    arc: "arc",
    generateRoute: "generateRoute",
    pointCloud: "pointCloud",
    roadMarking: "roadMarking",
    autoSketcher: "autoSketcher",
    measureArea: "measureArea",
    driveway: "driveway",
    lanemarkerTool: "lanemarkerTool",
    movingTool: "movingTool",
    turningArcTool: "turningArcTool",
    laneConnector: "laneConnector",
  };

  const toolsEnabled = useAppStore((s) => s.toolsEnabled);
  const userType = useAppStore((s) => s.userType);
  const { mapObjects } = useMapObjects();
  const setShowCameraInfo = useAppStore((s) => s.setShowCameraInfo);
  const setAppMode = useAppStore((s) => s.setAppMode);
  const [isShowPointCloudMenu, setIsShowPointCloudMenu] = useState(false);

  const isPrivileged = userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN);

  useEffect(() => {
    let isShow = false;
    if (mapObjects) {
      for (const key in mapObjects) {
        if (mapObjects[key].type === Type.ASSET.THREEDTILES) {
          isShow = true;
          break;
        }
      }
    }
    setIsShowPointCloudMenu(isShow);
    if (isShow) setAppMode(Type.MODE.POINTCLOUD);
    else setAppMode(Type.MODE.VIEW);
  }, [mapObjects, setAppMode]);

  useEffect(() => {
    setShowCameraInfo(isShowPointCloudMenu);
  }, [isShowPointCloudMenu, setShowCameraInfo]);

  const hasAnyEnabled = useMemo(() => {
    if (!toolsEnabled) return false;
    return Object.values(toolsEnabled).some(Boolean);
  }, [toolsEnabled]);

  const hasAnyMenu = hasAnyEnabled || isShowPointCloudMenu || Boolean(isPrivileged);

  if (!hasAnyMenu) return null;

  return (
    <Box position="absolute" top="225px" right="17px" zIndex={1002} maxHeight="calc(100vh - 325px)" pointerEvents="none">
      <Box display="flex" flexFlow="column wrap-reverse" gap={1} alignItems="flex-end" alignContent="flex-end" pointerEvents="auto" maxHeight="calc(100vh - 325px)">
        <CustomizeTools />

        {toolsEnabled?.[TOOL_IDS.measureDistance] && <MeasureDistance />}
        {toolsEnabled?.[TOOL_IDS.measureArea] && <AreaCalculation />}
        {toolsEnabled?.[TOOL_IDS.lineIntersectMeasure] && <LineIntersectMeasure />}
        {toolsEnabled?.[TOOL_IDS.quickSearch] && <QuickSearch />}
        {toolsEnabled?.[TOOL_IDS.showPoints] && <ShowPoints />}

        {toolsEnabled?.[TOOL_IDS.validationMenu] && <ValidationTool />}
        {toolsEnabled?.[TOOL_IDS.globalTools] && <GlobalTools />}

        {isPrivileged && toolsEnabled?.[TOOL_IDS.overlayText] && <OverlayText />}

        {isPrivileged && toolsEnabled?.[TOOL_IDS.trafficLight] && <TrafficLightTool />}
        {isPrivileged && toolsEnabled?.[TOOL_IDS.trafficSign] && <TrafficSignTool />}

        {isPrivileged && toolsEnabled?.[TOOL_IDS.lanelet] && <Lanelet />}
        {isPrivileged && toolsEnabled?.[TOOL_IDS.LaneletCheckByIds] && <LaneletCheckByIds />}
        {isPrivileged && toolsEnabled?.[TOOL_IDS.AutomaticConnectionCheck] && <AutomaticConnectionCheck />}

        {isPrivileged && toolsEnabled?.[TOOL_IDS.association] && <Association />}
        {isPrivileged && toolsEnabled?.[TOOL_IDS.arc] && <Arc />}
        {isPrivileged && toolsEnabled?.[TOOL_IDS.generateRoute] && <GenerateRoute />}

        {isShowPointCloudMenu && toolsEnabled?.[TOOL_IDS.pointCloud] && <PointCloud />}

        {isPrivileged && toolsEnabled?.[TOOL_IDS.roadMarking] && <RoadMarking />}

        {toolsEnabled?.[TOOL_IDS.autoSketcher] && <AutoSketcher />}
        {isPrivileged && toolsEnabled?.[TOOL_IDS.driveway] && <DrivewayTool />}

        {isPrivileged && toolsEnabled?.[TOOL_IDS.movingTool] && <MoveTool />}
        {isPrivileged && toolsEnabled?.[TOOL_IDS.lanemarkerTool] && <LaneMarkerStyleTool />}

        {isPrivileged && toolsEnabled?.[TOOL_IDS.turningArcTool] && <TurningArcTool />}

        {isPrivileged && toolsEnabled?.[TOOL_IDS.laneConnector] && <LaneConnectorTool />}
      </Box>
    </Box>
  );
}

export default SideBarRight;
